import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';

import { AuthProvider } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import InstallPrompt from '@/components/InstallPrompt';

import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import Landing from '@/pages/Landing';
import Dashboard from '@/pages/Dashboard';
import Assessment from '@/pages/Assessment';
import ScoreResult from '@/pages/ScoreResult';
import Coach from '@/pages/Coach';
import Library from '@/pages/Library';
import ProgressPage from '@/pages/Progress';
import Vault from '@/pages/Vault';
import Subscriptions from '@/pages/Subscriptions';
import Notifications from '@/pages/Notifications';
import AdminDashboard from '@/pages/AdminDashboard';

// Intercept Emergent Google Auth callback (session_id in URL fragment) before
// the normal router routes run. This must happen synchronously during render
// to avoid race conditions with the ProtectedRoute auth check.
function AppRouter() {
	const location = useLocation();
	if (location.hash?.includes('session_id=')) {
		return <AuthCallback />;
	}
	return (
		<Routes>
			<Route path="/login" element={<Login />} />
			<Route path="/register" element={<Register />} />
			<Route path="/forgot-password" element={<ForgotPassword />} />
			<Route path="/reset-password" element={<ResetPassword />} />

			<Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
			<Route path="/assessment" element={<ProtectedRoute><Assessment /></ProtectedRoute>} />
			<Route path="/score/:id" element={<ProtectedRoute><ScoreResult /></ProtectedRoute>} />
			<Route path="/score" element={<ProtectedRoute><ScoreResult /></ProtectedRoute>} />
			<Route path="/coach" element={<ProtectedRoute><Coach /></ProtectedRoute>} />
			<Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
			<Route path="/library/:slug" element={<ProtectedRoute><Library /></ProtectedRoute>} />
			<Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
			<Route path="/vault" element={<ProtectedRoute><Vault /></ProtectedRoute>} />
			<Route path="/subscription" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
			<Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

			<Route path="/admin" element={<ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}><AdminDashboard /></ProtectedRoute>} />
			<Route path="/super-admin" element={<ProtectedRoute roles={['SUPER_ADMIN']}><AdminDashboard /></ProtectedRoute>} />

			<Route path="/" element={<Landing />} />
			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	);
}

export default function App() {
	return (
		<div className="App">
			<BrowserRouter>
				<AuthProvider>
					<AppRouter />
					<InstallPrompt />
					<Toaster position="top-right" richColors />
				</AuthProvider>
			</BrowserRouter>
		</div>
	);
}
